import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { GenericTable } from '@app/components/GenericTable/GenericTable';
import { ReactNode } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ListResponse } from '@app/models';
import {
  ErrorLogInterface,
  ErrorLogType,
  ErrorLogLevel,
} from '@app/models/log/error';
import { DateRangePicker } from '@app/components/DateRangePicker/DateRangePicker';
import { UsersSelect } from '@app/components/UsersSelect/UsersSelect';
import { BusinessesSelect } from '@app/components/BusinessesSelect/BusinessesSelect';
import SearchIcon from '@app/assets/icons/search.svg?react';
import CalendarIcon from '@app/assets/icons/calendar-01.svg?react';
import { OrderDir, OrderDirType } from '@app/constants/core';
import {
  ERROR_LOG_TYPE_OPTIONS,
  ERROR_LOG_LEVEL_OPTIONS,
  getErrorLogTypeColor,
  getErrorLogLevelColor,
} from '@app/constants/error-log';
import { Select } from '@app/components/Select/Select';
import { IntegrationType } from '@app/utils/consts';
import { useNavigate } from 'react-router-dom';
import { getErrorLogs } from '@app/services/admin';
import { useDebounce } from '@app/utils/use-debounce';

const HEADER_COLUMNS = [
  { label: 'Date', field: 'createdAt', sortable: true },
  { label: 'Level', field: 'level', sortable: false },
  { label: 'Type', field: 'type', sortable: false },
  { label: 'User', field: 'user', sortable: false },
  { label: 'Business', field: 'business', sortable: false },
  { label: 'Integration', field: 'integrationType', sortable: false },
  { label: 'Message', field: 'message', sortable: false },
];

interface ErrorLogsResponse extends ListResponse<ErrorLogInterface> {}

interface FormType {
  orderBy: string;
  orderDir: OrderDirType;
  page: number;
  perPage: number;
  userId?: string;
  businessId?: string;
  integrationType?: IntegrationType;
  search: string;
  startDate?: string;
  endDate?: string;
  logType?: ErrorLogType;
  logLevel?: ErrorLogLevel;
}

export const AdminErrorLogs = () => {
  const navigate = useNavigate();
  const form = useForm<FormType>({
    defaultValues: {
      orderBy: 'createdAt',
      orderDir: OrderDir.desc,
      page: 1,
      perPage: 10,
      userId: undefined,
      businessId: undefined,
      integrationType: undefined,
      search: '',
      startDate: '',
      endDate: '',
      logType: undefined,
      logLevel: undefined,
    },
  });

  const {
    businessId,
    integrationType,
    logType,
    logLevel,
    orderBy,
    orderDir,
    page,
    perPage,
    search,
    userId,
    startDate,
    endDate,
  } = form.watch();

  const debouncedSearch = useDebounce(search);

  const { data, isLoading, isError } = useQuery<ErrorLogsResponse>({
    queryKey: [
      'logs',
      'error',
      {
        page,
        perPage,
        userId,
        businessId,
        integrationType,
        orderDir,
        orderBy,
        search: debouncedSearch,
        startDate,
        endDate,
        logType,
        logLevel,
      },
    ],
    queryFn: () =>
      getErrorLogs({
        page,
        perPage,
        orderBy,
        orderDir,
        ...(userId && { userId }),
        ...(businessId && { businessId }),
        ...(integrationType && { integrationType }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(logType && { type: logType }),
        ...(logLevel && { level: logLevel }),
        ...(debouncedSearch && { search: debouncedSearch }),
      }),
    placeholderData: keepPreviousData,
  });

  const totalPages = useMemo(() => {
    if (!data) return 0;
    const responseData = data as ErrorLogsResponse;
    return Math.ceil(responseData.total / responseData.perPage);
  }, [data]);

  const handlePrevPage = () => {
    if (page > 1) {
      form.setValue('page', page - 1);
    }
  };

  const handleNextPage = () => {
    if (data && page < totalPages) {
      form.setValue('page', page + 1);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Error Logs</h2>
          <div className="w-64 h-9 bg-gray-200 rounded-md animate-pulse"></div>
        </div>
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="h-16 bg-gray-100 rounded-md animate-pulse"
            ></div>
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-900">Error Logs</h2>
        <div className="p-4 bg-red-50 text-red-700 rounded-md">
          Error loading error logs. Please try again later.
        </div>
      </div>
    );
  }

  const handleLimitChange = (limit?: number) => {
    if (limit) {
      form.setValue('perPage', limit);
      form.setValue('page', 1);
    }
  };

  const handleSort = (name: string) => {
    form.setValue('orderDir', orderDir === 'desc' ? 'asc' : 'desc');
    form.setValue('orderBy', name);
  };

  const handleUserSelect = (selectedUserId?: string) => {
    form.setValue('userId', selectedUserId || undefined);
    form.setValue('page', 1);
  };

  const handleBusinessSelect = (selectedBusinessId?: string) => {
    form.setValue('businessId', selectedBusinessId || undefined);
    form.setValue('page', 1);
  };

  const handleLogTypeSelect = (selectedLogType?: ErrorLogType) => {
    form.setValue('logType', selectedLogType || undefined);
    form.setValue('page', 1);
  };

  const handleLogLevelSelect = (selectedLogLevel?: ErrorLogLevel) => {
    form.setValue('logLevel', selectedLogLevel || undefined);
    form.setValue('page', 1);
  };

  const handleIntegrationTypeSelect = (
    selectedIntegrationType?: IntegrationType
  ) => {
    form.setValue('integrationType', selectedIntegrationType || undefined);
    form.setValue('page', 1);
  };

  const renderErrorLogRow = (logs: ErrorLogInterface[]): ReactNode => {
    return logs.map((log) => (
      <tr
        key={log.id}
        className="hover:bg-gray-50 cursor-pointer"
        onClick={() => navigate(`/admin/logs/error/${log.id}`)}
      >
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {format(new Date(log.createdAt), 'PPpp')}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <span
            className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getErrorLogLevelColor(log.level)}`}
          >
            {log.level}
          </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <span
            className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getErrorLogTypeColor(log.type)}`}
          >
            {log.type.replace('_', ' ')}
          </span>
        </td>
        <td className="px-6 py-4">
          <div className="text-sm font-medium text-gray-900">
            {log.user?.firstName} {log.user?.lastName}
          </div>
          <div className="text-sm text-gray-500">{log.user?.email}</div>
        </td>
        <td className="px-6 py-4">
          <div className="text-sm font-medium text-gray-900">
            {log.business?.name}
          </div>
          <div className="text-sm text-gray-500">{log.business?.email}</div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {log.integrationType || '-'}
        </td>
        <td className="px-6 py-4 text-sm text-gray-900">
          <div className="font-medium truncate max-w-xs" title={log.message}>
            {log.message}
          </div>
        </td>
      </tr>
    ));
  };

  const handleFiltersReset = () => {
    form.reset();
    form.setValue('page', 1);
  };

  const hasActiveFilters = Boolean(
    search ||
    userId ||
    businessId ||
    integrationType ||
    startDate ||
    endDate ||
    logType ||
    logLevel
  );
  const hasData = Boolean(data?.data && data.data.length > 0);

  return (
    <FormProvider {...form}>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-2xl font-bold text-gray-900">Error Logs</h2>
        </div>
        <div className="bg-white w-full p-4 space-y-4">
          <input
            type="text"
            placeholder="Search error logs by message..."
            value={search}
            onChange={(e) => form.setValue('search', e.target.value)}
            className="w-full bg-gray-100 border border-gray-200 rounded-lg px-4 py-2 text-gray-600 placeholder:text-gray-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
          />
          <div className="flex flex-row gap-4 flex-wrap">
            <DateRangePicker
              startName="startDate"
              endName="endDate"
              startPlaceholder="Start date"
              endPlaceholder="End date"
              className="bg-gray-100 border-0 rounded-lg px-4 py-2 text-gray-600 placeholder:text-gray-500 focus:bg-gray-200 focus:outline-none !ml-0"
              containerClass="flex-shrink-0"
              position="absolute"
            />
            <UsersSelect
              onChange={handleUserSelect}
              value={userId}
              name="userId"
            />
            <BusinessesSelect
              onChange={handleBusinessSelect}
              value={businessId}
              name="businessId"
            />
            <Select
              options={ERROR_LOG_TYPE_OPTIONS}
              onChange={(value) => handleLogTypeSelect(value as ErrorLogType)}
              value={logType || ''}
              name="logType"
              placeholder="All Types"
            />
            <Select
              options={ERROR_LOG_LEVEL_OPTIONS}
              onChange={(value) => handleLogLevelSelect(value as ErrorLogLevel)}
              value={logLevel || ''}
              name="logLevel"
              placeholder="All Levels"
            />
            <Select
              options={[
                // { value: IntegrationType.YELP, label: 'Yelp' },
                { value: IntegrationType.GOOGLE, label: 'Google' },
                { value: IntegrationType.TRUSTPILOT, label: 'Trustpilot' },
                { value: IntegrationType.FACEBOOK, label: 'Facebook' },
              ]}
              onChange={(value) =>
                handleIntegrationTypeSelect(value as IntegrationType)
              }
              value={integrationType || ''}
              name="integrationType"
              placeholder="All Integrations"
            />
            {hasActiveFilters && (
              <button
                type="button"
                onClick={handleFiltersReset}
                className="flex items-center gap-1 text-red-500 hover:text-red-600 transition-colors cursor-pointer"
              >
                <span className="text-sm">Reset</span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 4L4 12M4 4L12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
        {!hasData && !isLoading && hasActiveFilters && (
          <>
            <br />
            <br />
            <br />
            <div className="flex items-center justify-center flex-col max-w-[350px] mx-auto space-y-4 h-full mt-20">
              <div className="rounded-lg bg-white p-4">
                <SearchIcon />
              </div>
              <p className="font-bold">No error logs</p>
              <p className="text-sm text-base-coolGray text-center">
                No error logs match your current filters.
                <br /> Consider adjusting them.
              </p>
            </div>
          </>
        )}
        {!hasData && !isLoading && !hasActiveFilters && (
          <div className="flex items-center justify-center flex-col max-w-[350px] mx-auto space-y-4 h-full mt-20">
            <div className="rounded-lg bg-white p-4">
              <CalendarIcon />
            </div>
            <p className="font-bold">No error logs</p>
            <p className="text-sm text-base-coolGray text-center">
              There are no error logs to display.
              <br />
              Error logs will appear here when system errors occur.
            </p>
          </div>
        )}
        {data?.data && data.data!.length > 0 && !isLoading && (
          <div className="overflow-auto bg-white">
            <GenericTable<ErrorLogInterface>
              data={data?.data || []}
              headers={HEADER_COLUMNS}
              renderRow={renderErrorLogRow}
              currentPage={page}
              isLoading={isLoading}
              currentSortBy="createdAt"
              currentSortOrder={orderDir as 'asc' | 'desc'}
              onSort={handleSort}
              pagination={{
                total: data?.total || 0,
                handleNextPage,
                handlePrevPage,
                handleLimitChange,
              }}
            />
          </div>
        )}
      </div>
    </FormProvider>
  );
};

export default AdminErrorLogs;
